import { CreateProjectDialog } from '@/app/pages/Projects/CreateProjectDialog'
import { CatchBoundary } from '@tanstack/react-router'
import { type ComponentType, Suspense, lazy } from 'react'
import { toast } from 'sonner'
import { useCommandPaletteStore } from './commandPaletteStore'
import { useCommandPaletteShortcut } from './useCommandPaletteShortcut'
import { usePendingPageActionRunner } from './usePendingPageActionRunner'

// Loaded on first open so the palette adds nothing to the initial bundle.
const loadCommandPaletteDialog = () =>
	lazy<ComponentType>(() =>
		import('./CommandPaletteDialog').then(
			module => ({ default: module.CommandPaletteDialog }),
			() => {
				// After a deploy the old chunk is gone. lazy caches the result, so swap in a fresh one to retry next open.
				CommandPaletteDialog = loadCommandPaletteDialog()
				useCommandPaletteStore.getState().setOpen(false)
				toast.error('Could not load the command palette. Reload the page to fix it.')
				return { default: () => null }
			},
		),
	)
let CommandPaletteDialog = loadCommandPaletteDialog()

export const preloadCommandPalette = () => void import('./CommandPaletteDialog')

const closeOnError = () => {
	useCommandPaletteStore.getState().setOpen(false)
	toast.error('The command palette ran into an error and closed. Try again.')
}

export function CommandPaletteHost() {
	useCommandPaletteShortcut()
	usePendingPageActionRunner()
	const isOpen = useCommandPaletteStore(s => s.isOpen)
	const isCreateProjectOpen = useCommandPaletteStore(s => s.isCreateProjectOpen)
	const setCreateProjectOpen = useCommandPaletteStore(s => s.setCreateProjectOpen)

	return (
		<>
			{isOpen && (
				<CatchBoundary getResetKey={() => 'command-palette'} onCatch={closeOnError} errorComponent={() => null}>
					<Suspense fallback={null}>
						<CommandPaletteDialog />
					</Suspense>
				</CatchBoundary>
			)}
			{isCreateProjectOpen && <CreateProjectDialog open onOpenChange={setCreateProjectOpen} />}
		</>
	)
}
